import { z } from "zod";

// Enum de status (deve bater com o enum do banco de dados)
export const CompanyStatus = z.enum(["PENDING", "ACTIVE", "SUSPENDED", "INACTIVE"]);
export type CompanyStatusType = z.infer<typeof CompanyStatus>;

// Regex para CNPJ
const cnpjRegex = /^\d{14}$|^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/;

// Lista de UFs válidas
const validUFs = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO",
  "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI",
  "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO",
] as const;

export const UF = z.enum(validUFs);
export type UFType = z.infer<typeof UF>;

// Schema para criação de empresa
export const createCompanySchema = z.object({
  cnpj: z
    .string()
    .min(1, "CNPJ é obrigatório")
    .regex(cnpjRegex, "CNPJ inválido. Use 14 números ou formato XX.XXX.XXX/XXXX-XX"),
  razaoSocial: z
    .string()
    .min(3, "Razão social deve ter no mínimo 3 caracteres")
    .max(255, "Razão social deve ter no máximo 255 caracteres"),
  nomeFantasia: z
    .string()
    .max(255, "Nome fantasia deve ter no máximo 255 caracteres")
    .optional()
    .nullable(),
  email: z
    .string()
    .email("Email inválido")
    .optional()
    .nullable()
    .or(z.literal("")),
  telefone: z
    .string()
    .max(20, "Telefone deve ter no máximo 20 caracteres")
    .optional()
    .nullable(),
  endereco: z
    .string()
    .optional()
    .nullable(),
  cidade: z
    .string()
    .max(100, "Cidade deve ter no máximo 100 caracteres")
    .optional()
    .nullable(),
  estado: z
    .string()
    .length(2, "Estado deve ter exatamente 2 caracteres (UF)")
    .toUpperCase()
    .refine((val) => !val || validUFs.includes(val as UFType), {
      message: "UF inválida",
    })
    .optional()
    .nullable()
    .or(z.literal("")),
  cep: z
    .string()
    .max(8, "CEP deve ter no máximo 8 caracteres")
    .regex(/^\d{0,8}$/, "CEP deve conter apenas números")
    .optional()
    .nullable()
    .or(z.literal("")),
});

export type CreateCompanyInput = z.infer<typeof createCompanySchema>;

// Schema para atualização de empresa (CNPJ não pode ser alterado)
export const updateCompanySchema = createCompanySchema.omit({ cnpj: true });

export type UpdateCompanyInput = z.infer<typeof updateCompanySchema>;

// Schema para resposta de empresa da API
export const companyResponseSchema = z.object({
  id: z.string().uuid(),
  cnpj: z.string(),
  razaoSocial: z.string(),
  nomeFantasia: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  telefone: z.string().nullable().optional(),
  endereco: z.string().nullable().optional(),
  cidade: z.string().nullable().optional(),
  estado: z.string().nullable().optional(),
  cep: z.string().nullable().optional(),
  status: CompanyStatus,
  createdAt: z.string().datetime().or(z.date()),
  updatedAt: z.string().datetime().or(z.date()),
  approvedAt: z.string().datetime().or(z.date()).nullable().optional(),
  createdBy: z.string().uuid().nullable().optional(),
  approvedBy: z.string().uuid().nullable().optional(),
});

export type CompanyResponse = z.infer<typeof companyResponseSchema>;

// Função helper para validar CNPJ
export function isValidCNPJ(cnpj: string): boolean {
  return cnpjRegex.test(cnpj);
}

// Função helper para formatar CNPJ
export function formatCNPJ(cnpj: string): string {
  const digits = cnpj.replace(/\D/g, "");
  if (digits.length !== 14) return cnpj;
  return digits.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    "$1.$2.$3/$4-$5"
  );
}

// Função helper para remover formatação do CNPJ
export function cleanCNPJ(cnpj: string): string {
  return cnpj.replace(/\D/g, "");
}

// Função helper para validar UF
export function isValidUF(uf: string): boolean {
  return validUFs.includes(uf.toUpperCase() as UFType);
}
